import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ArticleListItem, type TelegramChatOut, type TelegramMessageMeta } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { decodeEntities, stripHtml } from "@/lib/html";
import { groupByDay } from "@/lib/article";
import {
  avatarTone,
  channelIcon,
  channelNameOf,
  dominantChannel,
  emailFromAddress,
  initialsFor,
  isInternalNote,
  senderDisplayName,
} from "@/lib/articleChannel";
import { cn } from "@/lib/cn";
import { Avatar } from "@/components/ui/Avatar";
import { useShowAvatars } from "@/lib/showAvatars";
import { ArticleBodyRenderer } from "./ArticleBodyRenderer";
import { useTicketReplyDrafts } from "@/lib/replyDrafts";
import { AiOriginToggle, AiOriginTrace } from "./AiOriginBadge";
import { ArticleSecurityBadge } from "./ArticleSecurityBadge";
import { useArticleSecurity } from "./useArticleSecurity";
import { useAiOriginTrace } from "./useAiOriginTrace";
import { ArticleQuickActions } from "./ArticleQuickActions";
import { DraftBubble } from "./DraftPlaceholder";
import { SummaryMarker } from "./SummaryMarker";
import { useSummaryBoundary } from "./useSummaryBoundary";
import { useDeleteArticleNote } from "./useDeleteArticleNote";
import { AttachmentList } from "./ArticleTimeline";
import { useTelegramChat } from "@/lib/telegramChatApi";
import { TelegramContactHeader } from "./telegram/TelegramContactHeader";
import { WebchatSessionHeader } from "./webchat/WebchatSessionHeader";
import {
  TelegramBubbleActions,
  TelegramButtonPills,
  TelegramEditForm,
  TelegramQuote,
  TelegramRetractConfirm,
  TelegramStatusLine,
} from "./telegram/TelegramBubbleExtras";
import { useArticlePlainText, useTelegramMessageActions } from "./telegram/useTelegramMessage";
import { requestComposer } from "./telegram/composerBus";

/** Collapsed-preview length in characters — a rough proxy for "~6 lines" of
 * chat-bubble text without depending on CSS line-clamp (which doesn't work
 * against the sandboxed HTML iframe `ArticleBodyRenderer` uses for rich
 * bodies — see the collapsed/expanded split in `Bubble` below). */
const PREVIEW_CHARS = 400;

/** What a bubble on a Telegram ticket needs beyond its own article. `chat`
 * is null when the ticket has no Telegram chat (404) — then only the inline
 * attachments apply, no quote/status/actions. */
type TelegramBubbleContext = {
  chat: TelegramChatOut | null;
  meta: TelegramMessageMeta | undefined;
  lookup: ArticleListItem[];
};

function bubbleSide(senderType: string | null | undefined): "left" | "right" {
  const s = (senderType || "").toLowerCase();
  return s === "agent" ? "right" : "left";
}

/**
 * Article list "Variante B — Unterhaltung": chat-bubble layout for
 * WhatsApp/SMS/Chat-dominant tickets. Reuses the same filtered/chronological
 * article set the split view derives from `useArticleListState` — this
 * component only owns per-bubble expand state.
 */
export function ArticleConversationView({
  ticketId,
  articles,
  allArticles,
  canNote,
  canDelete = false,
  locale,
  newestFirst = false,
  composer,
}: {
  ticketId: number;
  /** Already filtered, chronological (oldest→newest) — see
   * `useArticleListState().chronological`. */
  articles: ArticleListItem[];
  /** Full, unfiltered article set — used to look up a draft's reply target
   * even when the active filter hides it from `articles`. Defaults to
   * `articles` when omitted (e.g. in tests). */
  allArticles?: ArticleListItem[];
  canNote: boolean;
  /** Whether the agent may delete internal notes (``rw`` permission). */
  canDelete?: boolean;
  locale: string;
  /** Messenger order: newest message (and the composer) on top, no
   * follow-to-bottom. Off = chronological with the composer below. */
  newestFirst?: boolean;
  /** Chat composer, placed under the contact header in newest-first order
   * and below the thread otherwise. */
  composer?: ReactNode;
}) {
  const { t } = useTranslation();
  const threadRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { boundaryId, createdAt } = useSummaryBoundary(ticketId, articles);
  const drafts = useTicketReplyDrafts(ticketId);
  const lookupArticles = allArticles ?? articles;
  const draftChannelName = (articleId: number): string | undefined => {
    const target = lookupArticles.find((a) => a.id === articleId);
    return target ? channelNameOf(target) : (dominantChannel(lookupArticles) ?? undefined);
  };

  const isTelegram = dominantChannel(lookupArticles) === "Telegram";
  const chatQ = useTelegramChat(ticketId, isTelegram);
  const chat = isTelegram ? (chatQ.data ?? null) : null;
  const metaById = new Map((chat?.messages ?? []).map((m) => [m.article_id, m]));
  // A messenger thread follows new messages; a mail thread only jumps on open.
  const isWebchat = dominantChannel(lookupArticles) === "WebChat";
  const followId = isTelegram || isWebchat ? (articles[articles.length - 1]?.id ?? null) : null;

  // Keep the newest message in view whenever this view mounts (tab switch),
  // the ticket changes, a new message arrives or the order flips: bottom of
  // the thread chronologically, its top in newest-first order (scrolling
  // only the thread there — the newest end is already where the page
  // starts). jsdom (tests) doesn't implement scrollIntoView.
  useEffect(() => {
    if (newestFirst) {
      if (threadRef.current) threadRef.current.scrollTop = 0;
    } else {
      bottomRef.current?.scrollIntoView?.({ block: "end" });
    }
  }, [ticketId, followId, newestFirst]);

  // Newest-first: whole day blocks newest day first, messages inside a
  // block newest first too, the day label on top of its block.
  const groups = groupByDay(newestFirst ? [...articles].reverse() : articles, locale);
  // Where the reply will appear once sent — the thread's newest end.
  const draftBubbles = drafts.map((d) => (
    <DraftBubble
      key={`draft-${d.articleId}`}
      draft={d}
      locale={locale}
      channelName={draftChannelName(d.articleId)}
    />
  ));

  const thread = (
    <div
      key="thread"
      ref={threadRef}
      className="max-h-[60vh] space-y-4 overflow-y-auto rounded-lg border border-hairline bg-surface p-3"
      data-testid="article-conversation"
      data-order={newestFirst ? "newest-first" : "oldest-first"}
    >
      {newestFirst && draftBubbles}
      {articles.length === 0 ? (
        <p className="text-sm text-muted">{t("ticket.noArticles")}</p>
      ) : (
        groups.map((g) => (
          <section key={g.day} className="space-y-2">
            <div className="flex items-center justify-center">
              <span
                data-testid="conversation-day"
                className="rounded-full bg-surface-subtle px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted"
              >
                {g.day}
              </span>
            </div>
            {g.items.map((a) => (
              <Fragment key={a.id}>
                {/* The marker sits between summarized and newer messages:
                    above the boundary message in newest-first order, below
                    it chronologically — same rule as the split view. */}
                {newestFirst && a.id === boundaryId && (
                  <SummaryMarker createdAt={createdAt} locale={locale} />
                )}
                {isInternalNote(a) ? (
                  <NotePill
                    ticketId={ticketId}
                    article={a}
                    canDelete={canDelete}
                    locale={locale}
                  />
                ) : (
                  <Bubble
                    ticketId={ticketId}
                    article={a}
                    canNote={canNote}
                    canDelete={canDelete}
                    locale={locale}
                    side={bubbleSide(a.sender_type)}
                    telegram={
                      isTelegram
                        ? { chat, meta: metaById.get(a.id), lookup: lookupArticles }
                        : undefined
                    }
                  />
                )}
                {!newestFirst && a.id === boundaryId && (
                  <SummaryMarker createdAt={createdAt} locale={locale} />
                )}
              </Fragment>
            ))}
          </section>
        ))
      )}
      {!newestFirst && draftBubbles}
      <div ref={bottomRef} />
    </div>
  );

  // Same wrapper with or without the header: swapping it in once the chat
  // loads would remount the scroll container and lose its scroll position.
  // Keyed siblings: flipping the order moves the composer and the thread
  // instead of remounting them (the text being typed survives).
  const composerSlot = composer ? (
    <div key="composer" data-testid="conversation-composer-slot">
      {composer}
    </div>
  ) : null;
  return (
    <div className="space-y-2">
      {chat && <TelegramContactHeader key="header" chat={chat} locale={locale} />}
      {isWebchat && <WebchatSessionHeader key="webchat-header" ticketId={ticketId} />}
      {newestFirst ? [composerSlot, thread] : [thread, composerSlot]}
    </div>
  );
}

function useArticleBody(ticketId: number, articleId: number) {
  return useQuery({
    queryKey: ["tickets", ticketId, "articles", articleId, "body"],
    queryFn: () => api.getArticleBody(ticketId, articleId),
  });
}

function Bubble({
  ticketId,
  article,
  canNote,
  canDelete,
  locale,
  side,
  telegram,
}: {
  ticketId: number;
  article: ArticleListItem;
  canNote: boolean;
  canDelete: boolean;
  locale: string;
  side: "left" | "right";
  telegram?: TelegramBubbleContext;
}) {
  const { t } = useTranslation();
  const showAvatars = useShowAvatars();
  const senderName = senderDisplayName(article.from_address) || t("ticket.unknownSender");
  const isSystem = (article.sender_type || "").toLowerCase() === "system";
  // Side alone is easy to lose while scrolling — pair it with a clear hue:
  // agent = accent (cobalt), customer = green, system = neutral. Border and
  // sender-name color repeat the hue so it also works for short bubbles.
  const tone = isSystem
    ? "border border-hairline bg-surface-subtle"
    : side === "right"
      ? "border border-accent/35 bg-accent/15 rounded-br-md"
      : "border border-green/35 bg-green/10 rounded-bl-md";
  const nameTone = isSystem ? "text-muted" : side === "right" ? "text-accent" : "text-green";
  const aiOrigin = useAiOriginTrace({ ticketId, articleId: article.id });
  const security = useArticleSecurity(ticketId, article);
  const tgActions = useTelegramMessageActions(ticketId, article.id);
  const editing = tgActions.mode === "editing";
  const editText = useArticlePlainText(ticketId, article.id, editing);
  const meta = telegram?.chat ? telegram.meta : undefined;
  const isAgent = (article.sender_type || "").toLowerCase() === "agent";
  const canQuote = Boolean(telegram?.chat) && canNote && channelNameOf(article) === "Telegram";
  const canModify = canQuote && isAgent && meta?.direction === "out" && !meta.retracted_at;
  const quotedId = meta?.reply_to_article_id ?? null;
  // An answered keyboard is settled — only an open one can be taken back.
  const canRemoveButtons =
    canModify && (meta?.buttons.length ?? 0) > 0 && meta?.answered_button === null;

  return (
    <div
      className="group flex flex-col gap-1"
      data-testid={`conversation-bubble-${article.id}`}
      data-side={side}
    >
      <div className={cn("flex", side === "right" ? "justify-end" : "justify-start")}>
        <div className={cn("flex max-w-[76%] items-start gap-2", side === "right" && "flex-row-reverse")}>
          <Avatar
            initials={initialsFor(article)}
            email={showAvatars ? emailFromAddress(article.from_address) : null}
            tone={avatarTone(article.sender_type)}
            size={24}
            className="mt-0.5"
          />
          <div className="relative min-w-0">
            <div className={cn("space-y-1 rounded-2xl px-3 py-2", tone)}>
              <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
                <span className={cn("font-semibold", nameTone)}>{senderName}</span>
                <span aria-hidden>{channelIcon(channelNameOf(article))}</span>
                {article.ai_origin && (
                  <AiOriginToggle
                    articleId={article.id}
                    compact
                    open={aiOrigin.open}
                    onToggle={aiOrigin.toggle}
                  />
                )}
                {security && (
                  <ArticleSecurityBadge articleId={article.id} security={security} compact />
                )}
                <span className="font-mono tabular-nums">{formatDateTime(article.create_time, locale)}</span>
                <span
                  className={cn(
                    "h-1.5 w-1.5 shrink-0 rounded-full",
                    article.is_visible_for_customer ? "bg-green" : "bg-escalation",
                  )}
                  title={
                    article.is_visible_for_customer
                      ? t("ticket.articleVisibleTooltip")
                      : t("ticket.articleInternalTooltip")
                  }
                />
              </div>
              {quotedId !== null && (
                <TelegramQuote
                  ticketId={ticketId}
                  quotedId={quotedId}
                  quoted={telegram?.lookup.find((x) => x.id === quotedId)}
                />
              )}
              {editing && editText.plain !== null ? (
                <TelegramEditForm
                  initial={editText.plain}
                  pending={tgActions.pending}
                  onSave={tgActions.save}
                  onCancel={tgActions.cancel}
                />
              ) : meta?.retracted_at ? (
                <div className="text-muted line-through" data-testid="telegram-retracted-body">
                  <BubbleBody ticketId={ticketId} article={article} />
                </div>
              ) : (
                <BubbleBody ticketId={ticketId} article={article} />
              )}
              {telegram && <AttachmentList ticketId={ticketId} articleId={article.id} compact />}
              {meta && <TelegramStatusLine meta={meta} />}
              {tgActions.mode === "confirmRetract" && (
                <TelegramRetractConfirm
                  pending={tgActions.pending}
                  onConfirm={tgActions.confirmRetract}
                  onCancel={tgActions.cancel}
                />
              )}
              {tgActions.error && (
                <p className="text-xs text-danger" data-testid={`telegram-action-error-${article.id}`}>
                  {tgActions.error}
                </p>
              )}
            </div>
            {meta?.direction === "out" && meta.buttons.length > 0 && (
              <div className={cn("mt-1 flex", side === "right" ? "justify-end" : "justify-start")}>
                <TelegramButtonPills buttons={meta.buttons} answered={meta.answered_button} />
              </div>
            )}
            {/* Hover/focus action island — same handlers as the split view's
                reading pane, just icon-only to fit a bubble. */}
            <div className="pointer-events-none absolute -top-3 right-1 opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100">
              <div className="pointer-events-auto flex items-center gap-0.5 rounded-md border border-hairline bg-surface p-0.5 shadow-sm">
                {canQuote && (
                  <TelegramBubbleActions
                    articleId={article.id}
                    canModify={canModify}
                    canEdit={canModify && meta?.editable === true}
                    onQuote={() =>
                      requestComposer(ticketId, { quoteArticleId: article.id, focus: true })
                    }
                    onEdit={tgActions.startEdit}
                    onRetract={tgActions.startRetract}
                    onRemoveButtons={
                      canRemoveButtons && !tgActions.pending ? tgActions.removeButtons : undefined
                    }
                  />
                )}
                <ArticleQuickActions
                  ticketId={ticketId}
                  article={article}
                  canNote={canNote}
                  canDelete={canDelete}
                  compact
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {article.ai_origin && (
        <AiOriginTrace articleId={article.id} open={aiOrigin.open} query={aiOrigin.query} />
      )}
    </div>
  );
}

/** Collapsed: stripped plain-text preview (cheap, no iframe). Expanded: the
 * real `ArticleBodyRenderer` (HTML iframe or `<pre>`), same as the split
 * view's reading pane. */
function BubbleBody({ ticketId, article }: { ticketId: number; article: ArticleListItem }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const bodyQ = useArticleBody(ticketId, article.id);

  if (bodyQ.isLoading) return <p className="text-sm text-muted">…</p>;
  if (!bodyQ.data) return null;

  const plain = bodyQ.data.is_html ? stripHtml(bodyQ.data.body) : decodeEntities(bodyQ.data.body);
  const isLong = plain.length > PREVIEW_CHARS;

  return (
    <div>
      {expanded ? (
        <ArticleBodyRenderer body={bodyQ.data.body} isHtml={bodyQ.data.is_html} className="text-sm" />
      ) : (
        <p
          className={cn(
            "whitespace-pre-wrap text-ink",
            // Mail bodies are plain text more often than not — monospace keeps
            // quoting/indentation/ASCII tables legible inside a bubble.
            bodyQ.data.is_html ? "text-sm" : "font-mono text-[12.5px] leading-relaxed",
          )}
        >
          {isLong ? `${plain.slice(0, PREVIEW_CHARS)}…` : plain}
        </p>
      )}
      {isLong && (
        <button
          type="button"
          data-testid={`conversation-expand-${article.id}`}
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] font-medium text-accent hover:underline"
        >
          {expanded ? `${t("ticket.showLess")} ⌃` : `${t("ticket.showFull")} ⌄`}
        </button>
      )}
    </div>
  );
}

function NotePill({
  ticketId,
  article,
  canDelete,
  locale,
}: {
  ticketId: number;
  article: ArticleListItem;
  canDelete: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const bodyQ = useArticleBody(ticketId, article.id);
  const del = useDeleteArticleNote(ticketId, article.id);
  const senderName = senderDisplayName(article.from_address) || t("ticket.unknownSender");
  const plain = bodyQ.data
    ? bodyQ.data.is_html
      ? stripHtml(bodyQ.data.body)
      : decodeEntities(bodyQ.data.body)
    : "";

  return (
    <div className="flex flex-col items-center gap-1" data-testid={`conversation-note-${article.id}`}>
      <div className="group relative max-w-[80%]">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            "w-full rounded-2xl border border-dashed border-escalation/50 bg-escalation/10 px-3 py-1.5 text-xs text-escalation",
            expanded ? "text-left" : "text-center",
          )}
        >
          <p className="font-semibold">
            📝 {t("ticket.internalNote")} · {senderName} · {formatDateTime(article.create_time, locale)}
          </p>
          <p className={cn(expanded ? "mt-1 whitespace-pre-wrap" : "truncate")}>
            {expanded ? plain : plain.slice(0, 60)}
          </p>
        </button>
        {canDelete && (
          <button
            type="button"
            aria-label={t("ticket.deleteNote")}
            data-testid={`article-delete-${article.id}`}
            onClick={(e) => {
              e.stopPropagation();
              void del.requestDelete();
            }}
            className="pointer-events-none absolute -top-2 right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-hairline bg-surface text-[10px] text-muted opacity-0 transition-opacity duration-100 hover:text-danger group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
          >
            ✕
          </button>
        )}
      </div>
      {del.errorMessage && (
        <p className="text-xs text-danger" data-testid={`article-delete-error-${article.id}`}>
          {del.errorMessage}
        </p>
      )}
      {canDelete && del.dialog}
    </div>
  );
}
